<template>
  <div class="form-card">
    <p class="form-card__title">Live Translation</p>
    <template v-if="!config.appOrigin || !config.apiOrigin">
      <button class="btn btn-primary" type="button" disabled>{{ teacher ? 'Open Live Translation' : 'View Live Captions' }}</button>
      <span class="translation-beta">Beta</span>
      <p>Live translation is being prepared.</p>
    </template>
    <template v-else-if="!classId">
      <button class="btn btn-primary" type="button" disabled>{{ teacher ? 'Open Live Translation' : 'View Live Captions' }}</button>
      <span class="translation-beta">Beta</span>
      <p>A class must be assigned before live translation is available.</p>
    </template>
    <template v-else-if="teacher">
      <a v-if="teacherUrl" class="btn btn-primary" :href="teacherUrl" target="_blank" rel="noopener noreferrer">Open Live Translation</a>
      <span class="translation-beta">Beta</span>
      <p>Choose your languages and start translation. Your student can then open captions from My Class.</p>
      <p>You may need to sign in again with your teacher account. Use desktop Chrome or Edge.</p>
    </template>
    <template v-else>
      <a v-if="studentUrl" class="btn btn-primary" :href="studentUrl" target="_blank" rel="noopener noreferrer">View Live Captions</a>
      <button v-else class="btn btn-primary" type="button" disabled>View Live Captions</button>
      <span class="translation-beta">Beta</span>
      <p v-if="state.status === 'error'" role="alert">
        Could not check live captions. Check your connection and try again.
        <button class="btn" type="button" @click="retry">Retry</button>
      </p>
      <p v-else-if="state.status === 'loading'" role="status">Checking for live captions...</p>
      <p v-else-if="!studentUrl" role="status">Waiting for your teacher to start live translation.</p>
      <p v-else>No microphone or screen sharing is needed. Captions open in a new tab.</p>
    </template>
    <p class="translation-beta-notice">Live translation is in beta. Captions may contain errors or be interrupted.</p>
  </div>
</template>

<script setup>
/* global defineProps */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { getClassLive } from '@/api/translation';
import { createClassLivePoller, studentCaptionUrl, teacherTranslationUrl, translationConfig } from '@/domain/liveTranslation';

const props = defineProps({ classId: { type: [String, Number], default: null }, role: { type: String, default: '' } });
const config = translationConfig(process.env.VUE_APP_TRANSLATION_APP_URL, process.env.VUE_APP_TRANSLATION_API_URL);
const teacher = computed(() => props.role.toLowerCase() === 'teacher');
const state = ref({ status: 'waiting' });
const teacherUrl = computed(() => teacherTranslationUrl(config.appOrigin, props.classId));
const studentUrl = computed(() => studentCaptionUrl(config.appOrigin, state.value));
const poller = createClassLivePoller({
    load: (id, signal) => getClassLive(config.apiOrigin, id, signal),
    onState: (value) => { state.value = value; },
});
function retry() {
    poller.stop();
    state.value = { status: 'waiting' };
    if (config.appOrigin && config.apiOrigin && props.classId && props.role.toLowerCase() === 'student') {
        poller.start(String(props.classId));
    }
}
watch(() => [props.classId, props.role], retry, { immediate: true });
onBeforeUnmount(poller.stop);
</script>

<style scoped>
.translation-beta {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.125rem 0.5rem;
  border-radius: 0.375rem;
  background: #fef3c7;
  color: #92400e;
  font-size: 0.6875rem;
  font-weight: 600;
  vertical-align: middle;
}
.translation-beta-notice {
  margin-top: 0.75rem;
  color: #64748b;
  font-size: 0.75rem;
}
</style>
